import { preflightMic, type MicPreflightResult } from '@orborix/voice-client';
import { markAudioStuck } from '../../../lib/audioStuck.js';

/**
 * Cuánto se espera al mic una vez que el permiso está dado. Con el permiso ya
 * concedido, `getUserMedia` resuelve en milisegundos; si en este tiempo no
 * contestó, el servidor de audio del sistema está trabado.
 */
export const MIC_PREFLIGHT_GRANTED_WAIT_MS = 6000;

/**
 * `preflightMic()` con tope. Caso real (encargo 51, 06/10/2026): en una PC con
 * el servidor de audio trabado (pipewire-pulse sin responder tras fallar un
 * auricular Bluetooth), `getUserMedia` no resuelve NUNCA; el arranque lo
 * esperaba antes de abrir el WebSocket y la pantalla quedaba «Coach está
 * pensando…» para siempre. Con el tope, la charla abre por escrito (el mismo
 * camino que «no hay mic») y el aviso de mic lo dice.
 *
 * Mientras el permiso esté en `prompt` (el cartel del navegador abierto, el
 * usuario decidiendo) NO corre el tope: arranca cuando el permiso pasa a
 * `granted`. Si el navegador no expone la Permissions API, corre de entrada.
 * Si el `getUserMedia` colgado resuelve tarde, `preflightMic` ya suelta las
 * pistas que abrió.
 */
export async function boundedMicPreflight(
  waitMs: number = MIC_PREFLIGHT_GRANTED_WAIT_MS,
): Promise<MicPreflightResult> {
  const probe = preflightMic();
  let timer: ReturnType<typeof setTimeout> | null = null;
  // Si el mic ya contestó, la consulta del permiso puede resolver DESPUÉS: sin
  // esto el tope se armaba con el pre-flight terminado, nadie lo cancelaba y a
  // los 6 s marcaba el audio como trabado (la app quedaba muda hasta recargar;
  // noche del 15/10, «Probar solo» de la charla asistida).
  let done = false;
  const timeout = new Promise<MicPreflightResult>((resolve) => {
    const arm = (): void => {
      if (timer || done) return;
      timer = setTimeout(() => {
        console.warn(
          `[voice] el mic no respondió en ${waitMs} ms con el permiso dado (¿servidor de audio trabado?): la charla sigue por escrito`,
        );
        // Sin respuesta con el permiso dado = el audio del sistema está
        // trabado: la sesión no va a tocar el audio (ver `audioStuck.ts`).
        markAudioStuck();
        resolve({ status: 'unavailable', error: new Error('mic preflight timeout') });
      }, waitMs);
    };
    if (typeof navigator.permissions?.query !== 'function') {
      arm();
      return;
    }
    navigator.permissions
      .query({ name: 'microphone' })
      .then((s) => {
        if (s.state !== 'prompt') arm();
        else s.addEventListener('change', () => s.state !== 'prompt' && arm());
      })
      .catch(arm);
  });
  try {
    return await Promise.race([probe, timeout]);
  } finally {
    done = true;
    if (timer) clearTimeout(timer);
  }
}
